import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  BrowserCacheLocation,
  CacheLookupPolicy,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from '@azure/msal-browser';
import { ENTRA_API_SCOPE, ENTRA_AUTHORITY, ENTRA_CLIENT_ID, LOCAL_USER, REDIRECT_PATH } from './constants.ts';

/**
 * 토큰을 쥐고 있는 쪽.
 *
 * 실물은 이 출처에서 도는 MSAL 이다. 한동안은 `auth.sfood.ai` 가 그 자리였고 화면은 그쪽을
 * 숨은 iframe 으로 띄워 액세스 토큰만 받았는데, 그 사이트가 아직 뜨지 않아 로그인을 다시
 * 여기로 들였다. **되돌릴 때 고칠 자리는 아래 [entraBroker] 하나다** — 그 인터페이스가 그대로
 * 있어서 세션도 로그인 화면도 서버도 손댈 것이 없다.
 *
 * 구현이 둘인 것은 개발에서 Entra 를 쓰지 않으려는 것이다(constants.ts 의 LOCAL_AUTH).
 */
export interface AuthBroker {
  /**
   * 화면을 띄우지 않고 받는다. 대화형이 필요하면 null 이고, 그 밖의 실패는 던진다.
   *
   * 그 둘을 가르는 것이 요점이다. 섞으면 네트워크가 잠깐 흔들린 것만으로 멀쩡한 사람이 로그인
   * 화면으로 쫓겨난다.
   */
  silent(): Promise<BrokerToken | null>;
  /** 팝업으로 로그인을 받는다. **사람이 누른 자리에서만** 부른다. 막혔으면 false. */
  signIn(): Promise<boolean>;
  /** 나간다. */
  signOut(): Promise<void>;
}

/** `expiresOn` 은 밀리초다. 모르면 null 이고, 그때는 담아 두지 않는다. */
export interface BrokerToken {
  readonly accessToken: string;
  readonly expiresOn: number | null;
}

/**
 * MSAL 을 세운다. 처음 쓸 때 한 번이다.
 *
 * v5 는 다른 무엇을 부르기 전에 `initialize()` 가 끝나 있어야 한다. 기동 때 미리 돌리지 않는
 * 것은 로그인이 필요 없는 길에서까지 그 값을 치르지 않으려는 것이고, 실패하면 기억해 둔 약속을
 * 버려 다음 부름이 다시 해 보게 한다.
 */
let starting: Promise<PublicClientApplication> | null = null;
const app = (): Promise<PublicClientApplication> => {
  starting ??= (async () => {
    const msal = new PublicClientApplication({
      auth: {
        clientId: ENTRA_CLIENT_ID,
        authority: ENTRA_AUTHORITY,
        // 상대 경로면 지금 출처에 붙는다. 그래서 등록할 주소가 배포 출처마다 하나씩이면 되고,
        // 여기에 호스트를 박아 두는 자리가 없다.
        redirectUri: REDIRECT_PATH,
        postLogoutRedirectUri: REDIRECT_PATH,
      },
      // 탭을 닫아도 남는다. 세션 저장소면 새 탭마다 다시 로그인이다.
      cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    });
    await msal.initialize();
    return msal;
  })().catch((cause: unknown) => {
    starting = null;
    throw new Error('로그인 모듈을 세우지 못했다', { cause });
  });
  return starting;
};

/**
 * 지금 로그인한 사람. 없으면 null 이다.
 *
 * 활성 계정을 함께 세워 둔다. 한 사람만 쓰는 화면이라 목록의 첫째가 곧 그 사람이고, 세워 두지
 * 않으면 부르는 자리마다 고르는 규칙을 또 적게 된다.
 */
const active = (msal: PublicClientApplication): AccountInfo | null => {
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (account !== null && msal.getActiveAccount() === null) msal.setActiveAccount(account);
  return account;
};

/**
 * 팝업이 안 열린 것인가. 사람이 닫은 것과 나누어야 한다.
 *
 * 막힌 사람에게 "다시 눌러 주세요" 라고 하면 헛돌고, 닫은 사람에게 "팝업을 허용하세요" 라고
 * 하면 없는 문제를 고치게 시킨다.
 */
const popupBlocked = (error: unknown): boolean =>
  error instanceof BrowserAuthError &&
  (error.errorCode === BrowserAuthErrorCodes.popupWindowError ||
    error.errorCode === BrowserAuthErrorCodes.emptyWindowError);

/** 사람이 팝업을 닫았거나 취소했다. 실패가 아니라 "아직 안 들어왔다" 이다. */
const cancelled = (error: unknown): boolean =>
  error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.userCancelled;

/**
 * 이 출처에서 도는 MSAL. 로그인은 팝업으로만 받는다.
 *
 * 리다이렉트가 아닌 까닭은 **팝업이 여는 자리를 한 곳으로 묶어 주기 때문**이다. 리다이렉트는
 * 돌아온 코드를 라우팅보다 먼저 교환해야 해서 기동 순서에 조건이 붙고(그 순서가 어긋나면 방금
 * 로그인한 사람이 가드에 걸려 로그인 화면으로 되돌아간다), 화면 상태도 한 번 날아간다.
 */
export const entraBroker: AuthBroker = {
  silent: async () => {
    const msal = await app();
    const account = active(msal);
    // 계정이 없으면 물어볼 것도 없다. MSAL 에 청하면 어차피 대화형이 필요하다고 답한다.
    if (account === null) return null;
    try {
      const result = await msal.acquireTokenSilent({
        account,
        scopes: [ENTRA_API_SCOPE],
        /*
         * 담아 둔 액세스 토큰과 갱신 토큰까지만 본다.
         *
         * 기본값은 그 둘이 다 만료됐을 때 숨은 iframe 으로 Entra 세션을 물어본다. 서드파티
         * 쿠키를 막는 브라우저에서 그 iframe 은 답을 못 받고 기본 10초를 다 쓴 뒤에야
         * 실패하는데, 그 10초가 라우팅을 막는 자리에 놓인다. 어느 브라우저에서나 같은 시점에
         * 같은 방식으로 실패하는 편을 골랐다.
         *
         * SPA 리디렉션 URI 로 발급되는 갱신 토큰은 24시간이 상한이라 하루에 한 번은 여기서
         * 걸리고, 그때는 사람이 로그인 화면에서 다시 누른다.
         */
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken,
      });
      if (result.accessToken === '') return null;
      return { accessToken: result.accessToken, expiresOn: result.expiresOn?.getTime() ?? null };
    } catch (error) {
      // 대화형이 필요하다는 것만 null 이다. 나머지는 네트워크나 Entra 쪽 사정이라 다시
      // 로그인해서 풀릴 일이 아니고, 그것까지 null 로 만들면 멀쩡한 세션이 쫓겨난다.
      if (error instanceof InteractionRequiredAuthError) return null;
      throw error;
    }
  },

  /**
   * 토큰을 여기서 내지 않는다. 팝업이 끝나면 저장소에 계정이 앉을 뿐이고, 토큰은 [silent] 가
   * 다시 받는다. 받는 길이 하나라 담아 두는 자리도 하나다.
   */
  signIn: async () => {
    const msal = await app();
    try {
      const result = await msal.loginPopup({ scopes: [ENTRA_API_SCOPE] });
      msal.setActiveAccount(result.account);
      return true;
    } catch (error) {
      if (popupBlocked(error)) return false;
      // 사람이 닫은 것은 참으로 답한다. 부르는 쪽이 곧바로 토큰을 물어 못 받고 "로그인이
      // 끝나지 않았습니다" 를 말한다 — 팝업이 막힌 것과 다른 말이다.
      if (cancelled(error)) return true;
      throw error;
    }
  },

  /**
   * 나간다. Entra 쪽 세션까지 끊는다.
   *
   * 팝업이 막히면 최상위 창이 로그아웃 화면으로 간다. 로그인과 달리 화면 상태를 지킬 까닭이
   * 없고, 아무 일도 없었던 것처럼 남는 것보다 낫다.
   *
   * 그 밖의 실패에서는 이 기계의 저장소만이라도 비운다. 부르는 쪽이 `void logout()` 이라 여기서
   * 던지면 아무도 받지 않고, 무엇보다 **나가려던 사람이 그대로 들어와 있는 것이 가장 나쁘다.**
   */
  signOut: async () => {
    const msal = await app();
    try {
      await msal.logoutPopup();
    } catch (error) {
      if (popupBlocked(error)) {
        await msal.logoutRedirect();
        return;
      }
      if (cancelled(error)) return;
      await msal.clearCache();
    }
  },
};

/**
 * 개발에서 Entra 자리를 메우는 것. 서명 없는 토큰을 그 자리에서 지어낸다.
 *
 * 서버가 `LOCAL_AUTH=true` 일 때 서명을 보지 않고 가운데 마디만 읽으므로
 * (apps/server/src/platform/auth.ts 의 localVerifier) 그 마디만 채운다. **바뀌는 것은 "이
 * 토큰이 진짜인가" 하나뿐이다** — 데이터는 실제 백엔드가 답하고, 역할 때문에 잠기는 자리는
 * 여기서도 잠긴다.
 *
 * 나가는 길은 여기서 할 일이 없다. 세션이 사는 곳이 없기 때문이다. 로그아웃을 누르면 화면은
 * 로그인으로 가지만 곧 다시 열린다.
 */
export const localBroker: AuthBroker = {
  silent: async () => ({
    accessToken: `${base64url('{"alg":"none"}')}.${base64url(
      JSON.stringify({
        sub: 'local-dev',
        // 서버가 사람을 찾는 자연키다. 고정해 두어야 같은 사람으로 열린다.
        oid: 'local-dev',
        preferred_username: LOCAL_USER.email,
        name: LOCAL_USER.name,
      }),
    )}.`,
    expiresOn: Date.now() + 60 * 60 * 1_000,
  }),
  signIn: async () => true,
  signOut: async () => {},
};

/**
 * UTF-8 로 읽어 base64url 로 적는다.
 *
 * `btoa` 는 바이트마다 한 글자인 문자열만 받으므로 한글 이름을 그대로 넣으면 던진다. 서버도
 * 같은 이유로 `atob` 의 결과를 UTF-8 로 다시 읽는다.
 */
const base64url = (json: string): string => {
  const bytes = new TextEncoder().encode(json);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
};
